<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ContactAPI from 'dashboard/api/contacts';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

// Channel selector for a resolved contact. Fetches the contact's reachable
// channels (contact_inboxes) and lets the agent choose which one the request
// is delivered through. The empty "Automatic" option keeps the default email
// routing (and token-link fallback), so picking a channel is always optional.
const props = defineProps({
  contactId: { type: [String, Number], default: '' },
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const channels = ref([]);
const isLoading = ref(false);

const channelTypeLabel = channelType => {
  const key = (channelType || '').replace('Channel::', '').toUpperCase();
  const map = {
    EMAIL: t('DOCUMENTS.CHANNEL_PICKER.TYPES.EMAIL'),
    WHATSAPP: t('DOCUMENTS.CHANNEL_PICKER.TYPES.WHATSAPP'),
    TWILIOSMS: t('DOCUMENTS.CHANNEL_PICKER.TYPES.SMS'),
    SMS: t('DOCUMENTS.CHANNEL_PICKER.TYPES.SMS'),
    TELEGRAM: t('DOCUMENTS.CHANNEL_PICKER.TYPES.TELEGRAM'),
    API: t('DOCUMENTS.CHANNEL_PICKER.TYPES.API'),
  };
  return map[key] || t('DOCUMENTS.CHANNEL_PICKER.TYPES.API');
};

const channelOptions = computed(() => {
  const options = [{ value: '', label: t('DOCUMENTS.CHANNEL_PICKER.AUTOMATIC') }];
  channels.value.forEach(channel => {
    options.push({
      value: channel.id,
      label: `${channelTypeLabel(channel.inbox?.channel_type)} · ${channel.inbox?.name}`,
    });
  });
  return options;
});

const fetchChannels = async id => {
  if (!id) {
    channels.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.showWithContactInboxes(id);
    channels.value = data?.payload?.contact_inboxes || [];
  } catch {
    channels.value = [];
  } finally {
    isLoading.value = false;
  }
};

// A new contact means the previously picked channel no longer applies; reset
// to automatic and load the new contact's channels.
watch(
  () => props.contactId,
  id => {
    emit('update:modelValue', '');
    fetchChannels(id);
  }
);

const handleSelect = value => emit('update:modelValue', value);
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <template v-if="contactId">
      <label v-if="label" class="block text-sm font-medium text-n-slate-12">
        {{ label }}
      </label>
      <ComboBox
        :model-value="modelValue"
        :options="channelOptions"
        :placeholder="t('DOCUMENTS.CHANNEL_PICKER.PLACEHOLDER')"
        @update:model-value="handleSelect"
      />
      <span class="text-xs text-n-slate-11">
        {{ isLoading ? t('DOCUMENTS.CHANNEL_PICKER.LOADING') : t('DOCUMENTS.CHANNEL_PICKER.HINT') }}
      </span>
    </template>
  </div>
</template>
